import React, { useCallback, useState, useEffect } from 'react';
import { Drawer } from '@silverfort/sf-components';
import { IncidentsConfiguration, SaveIncidentConfigPayload } from '../types';
import useGetExclusions from '../../api/queries/exclusions/useGetExclusions';
import { IncidentExclusionProvider } from '../Exclusions/incidentExclusionContext';
import IncidentConfigurationContent from './IncidentConfigurationContent';
import DiscardChangesPopup from '../Exclusions/DiscardChangesPopup/DiscardChangesPopup';

export interface IncidentConfigurationDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (params: SaveIncidentConfigPayload) => void;
    isSaving: boolean;
    incidentConfiguration: IncidentsConfiguration | null;
}

const IncidentConfigurationDrawer: React.FC<IncidentConfigurationDrawerProps> = ({
    isOpen,
    onClose,
    incidentConfiguration,
    onSave,
    isSaving
}) => {
    const [config, setConfig] = useState<IncidentsConfiguration | null>(incidentConfiguration);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setConfig(incidentConfiguration);
        setDirty(false);
    }, [incidentConfiguration]);

    const { data: exclusionsData = [], isFetching: exclusionsFetching } = useGetExclusions(
        incidentConfiguration?.kindLabel ?? ''
    );

    const [isDiscardPopupOpen, setIsDiscardPopupOpen] = useState(false);

    const onDiscardChanges = () => {
        setIsDiscardPopupOpen(false);
        onClose?.();
    };

    const close = useCallback(() => {
        if (dirty) setIsDiscardPopupOpen(true);
        else onClose?.();
    }, [dirty, onClose]);

    return (
        <Drawer
            open={isOpen}
            onClose={close}
            anchor="right"
            contentClassName="p-[0px] !p-[0px]"
            footerActionTitle="Close"
            headerDescription={`${config?.description}`}
            headerTitle={config?.name}>
            {config && (
                <IncidentExclusionProvider exclusions={exclusionsData} exclusionsLoading={exclusionsFetching}>
                    <IncidentConfigurationContent
                        config={config}
                        setConfig={setConfig}
                        onSave={onSave}
                        isSaving={isSaving}
                        setDirty={setDirty}
                        dirty={dirty}
                    />
                </IncidentExclusionProvider>
            )}

            <DiscardChangesPopup
                open={isDiscardPopupOpen}
                onDiscard={onDiscardChanges}
                onClose={() => setIsDiscardPopupOpen(false)}
            />
        </Drawer>
    );
};

export default IncidentConfigurationDrawer;
